import { coerceNumberProperty } from '@angular/cdk/coercion';

import { isNonNullable } from '@qtek/shared/utils';
import { FilterBase, FilterChangeBase } from './base';

/**
 * Compare value to other property on same object,
 * or given value if id is not present.
 */
interface EqualTo {
  /** Translation key to display. */
  name: string;

  /** Path to property. */
  id?: string;

  /** Value to compare against. */
  value?: number;

  /** Whether this option is selected. */
  selected?: boolean;
}

export interface NumericValues {
  /** Minimum value. `null` means no minimum value. */
  min?: number | null;

  /** Maximum value. `null` means no maximum value. */
  max?: number | null;

  /** List of values to should be equal to given value. */
  equalTo?: EqualTo[];
}

export interface NumericFilter extends FilterBase<NumericValues> {
  type: 'numeric';
}

export type NumericFilterChange = FilterChangeBase<
  NumericValues,
  NumericFilter
>;

/**
 * Check if provided value is equal to all values provided in configs.
 *
 * @param configs List of values that should be checked.
 * @param value Value of field to compare to.
 * @param row Row of data if comparison is against id.
 */
function equalToFilter<T>(
  configs: EqualTo[],
  value: number,
  row?: T,
  valueAccessor?: (row: T, id: string) => any
): boolean {
  return configs
    .filter(config => config.selected)
    .every(config => {
      const valueToCompare = config.id
        ? coerceNumberProperty(valueAccessor(row, config.id))
        : config.value;

      return valueToCompare === value;
    });
}

/**
 * Predicate function for numeric filter.
 * Returns `true` if row of data passes every filter.
 *
 * ```typescript
 * const data = {
 *   age: 25,
 * };
 *
 * const filters = [
 *   {
 *     id: 'age',
 *     min: 18,
 *     max: null,
 *   },
 * ];
 *
 * numericFilter(filters, data); // true
 * ```
 *
 * @param filters List of filter configs.
 * @param row Row of data to determine status for.
 */
export function numericFilter<T>(
  filters: NumericFilterChange[],
  row: T,
  valueAccessor: (row: T, id: string) => any
): boolean {
  return filters.every(({ values, config }) => {
    const value = coerceNumberProperty(valueAccessor(row, config.id));
    const isMoreOrEqualMin = isNonNullable(values.min)
      ? value >= values.min
      : true;
    const isLessOrEqualMax = isNonNullable(values.max)
      ? value <= values.max
      : true;
    const equalsToValue = isNonNullable(values.equalTo)
      ? equalToFilter(values.equalTo, value, row, valueAccessor)
      : true;

    return isMoreOrEqualMin && isLessOrEqualMax && equalsToValue;
  });
}
